import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { defineMessages, injectIntl, FormattedMessage } from 'react-intl';
import { Button, Container } from 'reactstrap';
import isEmpty from 'lodash/isEmpty';
import classNames from 'classnames';
import get from 'lodash/get';

import { getGift, acceptGift } from 'Redux/ducks/gift';
import { supportUrl } from 'Config/lang';
import { history } from 'Redux/store';
import { toggleLoginModal } from 'Redux/ducks/modal';
import BeforeRedeemableSection from 'Components/Gift/BeforeRedeemableSection';
import BundleCarousel from 'Components/Bundle/BundleCarousels/BundleCarousel';
import getUrlParameter from 'Helpers/Url/GetUrlParameter';
import GiftThemeHeader from 'Components/Gift/GiftThemeHeader';
import Loading from 'Components/Utility/Loading';
import Meta from 'Components/Utility/SEO/Meta';
import NoIndex from 'Components/Utility/SEO/NoIndex';
import RedeemableSection from 'Components/Gift/RedeemableSection';
import SplashHeader from 'Components/Banner/SplashHeader';
import RedeemGiftProductCard from 'Components/Gift/RedeemGiftProductCard';

let hasLoaded = false;

const messages = defineMessages({
  title: {
    id: 'redeemgift.title',
    defaultMessage: 'Redeem Gift',
  },
  from: {
    id: 'redeemgift.from',
    defaultMessage: 'from',
  },
  splash_header_title: {
    id: 'redeemgift.spash_header.title',
    defaultMessage: 'Fanatical Gift Redeem',
  },
  you_have_recived_a_gift: {
    id: 'redeemgift.you_have_recived_a_gift',
    defaultMessage: 'You have received a gift',
  },
});

const RedeemGiftContainer = ({
  intl: { formatMessage },
  authenticated,
  toggleLoginModal: launchLoginModal,
}) => {
  const [gift, setGift] = useState({});
  const [cardSelectedGame, setCardSelectedGame] = useState(1);
  const [unRevealedSlugs, setUnrevealedSlugs] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const code = getUrlParameter('giftcode') || '';

  useEffect(() => {
    const loadGift = async () => {
      if (!code) return false;

      const fetchedGift = await getGift(code);
      hasLoaded = true;
      const _unRevealedSlugs = get(fetchedGift, 'products', []).map(p => p.slug);
      const recipientHasRedeemed = get(fetchedGift, 'recipientHasRedeemed', false);
      if (!recipientHasRedeemed && !loaded) setUnrevealedSlugs(_unRevealedSlugs);
      setLoaded(true);
      return setGift(fetchedGift);
    };

    loadGift();

    const christmasFont = document.createElement('link');
    const birthdayFont = document.createElement('link');
    christmasFont.href = 'https://fonts.googleapis.com/css2?family=Dosis:wght@300;400;700&display=swap';
    birthdayFont.href = 'https://fonts.googleapis.com/css2?family=Press+Start+2P:wght@300;400;700&display=swap';

    christmasFont.type = 'text/css';
    birthdayFont.type = 'text/css';

    christmasFont.rel = 'stylesheet';
    birthdayFont.rel = 'stylesheet';

    document.head.appendChild(christmasFont);
    document.head.appendChild(birthdayFont);
  }, [code, authenticated, loaded]);


  const {
    gift_from: giftFrom,
    personal_message: personalMessage,
    products,
    isPreview,
    orderId,
    recipientHasRedeemed,
    theme,
  } = gift;

  let totalGameCount = 0;

  if (products && products.length) {
    products.forEach((game) => {
      totalGameCount += 1;
      game.totalGameIndex = totalGameCount; // eslint-disable-line no-param-reassign
    });
  }

  const productObject = {
    name,
    bundles: [],
    type: 'bundle',
    display_type: 'pick-and-mix',
  };

  const switchCarouselGame = (gameNumber) => {
    setCardSelectedGame(gameNumber);
  };


  let regionUnavailableProduct = false;

  if (products && products.length) {
    regionUnavailableProduct = products.filter(product => !product.regionAvailable).length > 0;
    products.sort((a, b) => b.regionAvailable - a.regionAvailable);
  }

  const giftFromTitle = `${formatMessage(messages.you_have_recived_a_gift)} ${giftFrom ? `${formatMessage(messages.from)} ${giftFrom}` : ''}`;

  const isRedeemable = authenticated && !isEmpty(gift) && hasLoaded;

  const push = history.push;

  const themes = ['christmas', 'birthday', 'thanksgiving', 'chinesenewyear'];

  const isStandardTheme = !themes.includes(theme);

  const productSlugs = (products || []).map(p => p.slug);

  const hasRevealedAGift = productSlugs.length !== unRevealedSlugs.length;

  return (
    <div
      className={
        classNames('redeem-page',
          {
            'redeem-page-christmas': theme === 'christmas',
            'redeem-page-birthday': theme === 'birthday',
            'redeem-page-thanksgiving': theme === 'thanksgiving',
            'redeem-page-chinese-new-year': theme === 'chinesenewyear',
          })
      }
    >
      <Meta brandTitle title={formatMessage(messages.title)} />
      <NoIndex />


      <div className="header-container">
        { isStandardTheme && loaded &&
          <div className="about-us pb-3">
            <SplashHeader
              title={
                !isEmpty(gift) ? giftFromTitle : formatMessage(messages.you_have_recived_a_gift)
              }
            />
          </div>
        }

        {!isStandardTheme && loaded &&
          <GiftThemeHeader theme={theme} />
        }

      </div>

      { !isStandardTheme && loaded &&
        <div className="gift-from-title">
          {giftFromTitle}
        </div>
      }

      <Container fluid className="p-0">
        <Container className="redeem-content-container">
          {isPreview && authenticated &&
            <Container className="pt-4">
              <div className="preview-container">
                <h1>
                  <FormattedMessage id="redeemgift.yourgiftpreview" defaultMessage="Your Gift Preview" />
                </h1>
                <div>
                  <FormattedMessage
                    id="redeemgift.giftsenderwarning"
                    defaultMessage="As the gift sender you cannot accept this gift. However here are the details of your gift as your friend will see them. To send your gift simply share the redeem link."
                  />
                </div>
              </div>
            </Container>
          }
          <div className="personal-message-container">
            <h3>
              {personalMessage}
            </h3>
          </div>

          <div className="gift-products-container">
            {products && products.length &&
              <div className="fan-grid-container">
                {products.map((product, index) =>
                  <div
                    className={classNames({ 'three-column-row': theme !== 'chinesenewyear', 'two-column-row': theme === 'chinesenewyear' })}
                    key={product._id}
                    style={{ zIndex: (index + 5) }}
                  >
                    <RedeemGiftProductCard
                      theme={theme}
                      product={product}
                      shouldHighlight={regionUnavailableProduct}
                      switchCarouselGame={switchCarouselGame}
                      setUnrevealedSlugs={setUnrevealedSlugs}
                      unRevealedSlugs={unRevealedSlugs}
                      recipientHasRedeemed={recipientHasRedeemed}
                    />
                  </div>,
                )}
              </div>
            }
          </div>

          {authenticated && !hasLoaded && code && <Loading /> }


          {(!code || (isEmpty(gift) && code && hasLoaded) || regionUnavailableProduct) &&
            <div className="error-container">
              { !code &&
                <p className="sorry-message">
                  <FormattedMessage
                    id="redeemgift.sorryinvalid"
                    defaultMessage="Sorry - this gift redeem link is invalid."
                  />
                </p>
              }
              { isEmpty(gift) && code && hasLoaded &&
                <p className="sorry-message">
                  <FormattedMessage
                    id="redeemgift.sorryexpired"
                    defaultMessage="Sorry - you cannot accept this gift order because it has expired."
                  />
                </p>
              }

              {regionUnavailableProduct &&
                <div className="sorry-message">
                  <FormattedMessage
                    id="redeemgift.onlyhighlighted"
                    defaultMessage="* Only highlighted products will activate in your country."
                  />
                </div>
              }
            </div>
          }


          {!authenticated &&
            <div className="please-sign-in">
              <FormattedMessage
                id="redeemgift.pleasesignin"
                defaultMessage="Please sign in or create an account to get your gift."
              />
            </div>
          }

          <div className="sign-in-accept-button-container">
            {!authenticated && code &&
              <Button color="primary" size="lg" onClick={launchLoginModal}>
                <FormattedMessage id="redeemgift.signincreateaccount" defaultMessage="sign in / create account" />
              </Button>
            }

            {authenticated && !isEmpty(gift) && !isPreview && !recipientHasRedeemed &&
              <Button
                color="primary"
                size="lg"
                disabled={isPreview}
                onClick={() => acceptGift(code)}
              >
                <FormattedMessage
                  id="redeemgift.acceptgift"
                  defaultMessage="Accept Gift"
                />
              </Button>
            }

            {authenticated && !isEmpty(gift) && isPreview &&
              <Button
                color="primary"
                size="lg"
                onClick={() => push(`/orders/${orderId}`)}
              >
                <FormattedMessage
                  id="redeemgift.viewandmanagegiftorder"
                  defaultMessage="View and Manage Gift Order"
                />
              </Button>
            }

            {authenticated && !isEmpty(gift) && recipientHasRedeemed &&
              <Button
                color="primary"
                size="lg"
                onClick={() => push(`/orders/${orderId}`)}
              >
                <FormattedMessage
                  id="redeemgift.viewgiftorder"
                  defaultMessage="View Gift Order"
                />
              </Button>
            }
          </div>

          {isRedeemable ?
            <RedeemableSection
              personalMessage={personalMessage}
              products={products}
              regionUnavailableProduct={regionUnavailableProduct}
            />

            :

            <BeforeRedeemableSection />
          }

          {products && products.length &&
            <React.Fragment>
              <div className="referral-title">
                <h3>
                  <FormattedMessage id="redeemgift.producdetails" defaultMessage="Product Details" />
                </h3>
              </div>
              <div className="brand-line my-3 mx-auto" />

              <div className="under-section-margin" />

              <BundleCarousel
                hideTitle
                lightThemeOverride
                allGames={products}
                totalGameCount={totalGameCount}
                product={productObject}
                selectedSlide={cardSelectedGame}
                slugsToBlur={unRevealedSlugs}
                hasRevealedAGift={hasRevealedAGift}
              />

            </React.Fragment>
          }

          <div className="gift-terms">
            <FormattedMessage
              id="redeemgift.findoutmore"
              defaultMessage="Find out more about gifting in this {supportlink}."
              values={{
                supportlink: <a href={`${supportUrl()}/articles/360015776518`} target="_blank" rel="noopener noreferrer" className="brand-link">
                  <FormattedMessage
                    id="redeemgift.info-extra.support-link"
                    defaultMessage="support article"
                  />
                </a>,
              }}
            />
          </div>
        </Container>
      </Container>
    </div>
  );
};


RedeemGiftContainer.propTypes = {
  authenticated: PropTypes.bool,
  toggleLoginModal: PropTypes.func.isRequired,
};

RedeemGiftContainer.defaultProps = {
  authenticated: false,
};

const mapStateToProps =
({
  auth: { authenticated },
}) => ({
  authenticated,
});

const mapDispatchToProps = { toggleLoginModal };

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(RedeemGiftContainer));
